import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  AfterViewChecked,
  ChangeDetectionStrategy,
  OnDestroy,
} from "@angular/core";
import { Util } from "src/app/shared/util";
import { IndicatorColourBank } from "../../colour-bank";
import { FacetsLib } from "../../facets-lib";
import { IndicatorState } from "../../indicator.state";
import { LegendComponent } from "../../legend/legend.component";
import { Chart } from "../chartData";
import ResizeObserver from "resize-observer-polyfill";
import { IndicatorType, VisualisationDownloadFormat } from "../../constraints";
import { DocumentExportService } from "../../../services/documentexport.service";
import { FacetLayout } from "../faceted-layout";
import { PlotlyGraphView } from "../plotly-graph-view";
import { PlotlyServiceExt } from "../../../services/plotly-service-extension";
import {MeasureService} from "../../../services/measure.service";
import { AppConfig } from "../../../app.config";
const enum ChartDrawingStageEnum {
  Created,
  Initialised,
  StateUpdated,
  DrawLegend,
  DrawingFirstSetOfCharts,
  DrawingSecondSetOfCharts,
}

const YAXIS_HEADROOM = 1.05; // factor
const Y_AXIS_OFFSET = 64; // px
const MIN_HEIGHT = 200; // Minimum height of chart
const MIN_WIDTH = 200; // Minimum width of chart
const CHARTVIEW_DISPLAY_PADDING = 16; // px
const MAX_ANNOTATION_OFFSET = 50; // px

@Component({
  selector: "(app-trend-view)",
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: "./trend-view.component.html",
  styleUrls: ["./trend-view.component.css"],
})
export class TrendViewComponent extends PlotlyGraphView
  implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild("canvas", {static: true}) canvas: ElementRef;
  @ViewChild(LegendComponent, {static: true})
  private legend: LegendComponent;

  state: IndicatorState;
  facetedPlotData: any;
  allCharts: Chart[];
  charts: Chart[];
  config: any;
  elementRef: ElementRef;
  facetLayoutStyle: string;
  canvasWidth: number;
  canvasHeight: number;
  observer: ResizeObserver;
  chartDrawingStage: ChartDrawingStageEnum = ChartDrawingStageEnum.Created;
  showLegend: boolean = true;
  measureService : MeasureService;

  allPeriods: number[] = [];
  missingPeriods: number[] = [];
  shouldAddMissingPeriods = false;
  isPresummarised = false;
  annotations: { [period: string]: string } = {};

  constructor(element: ElementRef, private cdRef: ChangeDetectorRef, documentExportService: DocumentExportService, plotlyServiceExt: PlotlyServiceExt) {
    super(documentExportService, plotlyServiceExt);
    this.elementRef = element;
    this.measureService = new MeasureService();
  }

  public exportAs(visualisationDownloadFormat: VisualisationDownloadFormat, source: string, fileName: string): any {
    if (visualisationDownloadFormat == VisualisationDownloadFormat.PNG) {
      super.exportAsPNG(fileName)
    }
    else {
      this.allCharts.forEach(chart => {
        const title = super.CreateChartTitle(this.state.Meta.title, this.state.ChartTitle, chart);
        super.exportAsSVG(chart.Id, title, !!this.facetedPlotData[0] ? this.facetedPlotData[0].traces : null, source, `${fileName}-${chart.Id}`);
      });
    }
  }

  memoriseCanvasSize(width: number, height: number) {
    if (width !== undefined && height !== undefined) {
      width = Math.floor(width);
      height = Math.floor(height);
      if (
        width !== 0 &&
        height != 0 &&
        (width !== this.canvasWidth || height !== this.canvasHeight)
      ) {
        this.canvasWidth = width;
        this.canvasHeight = height;
        this.onCanvasSizeChange();
      }
    }
  }

  ngOnInit() {
    this.memoriseCanvasSize(
      this.canvas.nativeElement.clientWidth,
      this.canvas.nativeElement.clientHeight
    );

    this.facetLayoutStyle = "100%";
    this.chartDrawingStage = ChartDrawingStageEnum.Initialised;
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.unobserve(this.canvas.nativeElement);
    }
  }

  ngAfterViewChecked() {
    this.memoriseCanvasSize(
      this.canvas.nativeElement.clientWidth,
      this.canvas.nativeElement.clientHeight
    );
  }

  get_grid_width(): number {
    return this.canvasWidth - CHARTVIEW_DISPLAY_PADDING * 2;
  }

  get_grid_heght(): number {
    return this.canvasHeight;
  }

  onCanvasSizeChange() {
    if (this.chartDrawingStage !== ChartDrawingStageEnum.Created) {
      this.drawFirstSetOfCharts();
    }
  }

  onStateUpdated(newState: IndicatorState) {
    this.chartDrawingStage = ChartDrawingStageEnum.StateUpdated;
    this.state = newState;
    this.isPresummarised = this.state.Meta?.typeId == IndicatorType.Presummarised;
    this.shouldAddMissingPeriods = this.isPresummarised && AppConfig.settings.featureSettings.showChartMissingPeriods;
    this.annotations = this.state.Annotations || {};
    this.drawLegend();
    this.drawFirstSetOfCharts();
  }

  drawFirstSetOfCharts() {
    if (!!this.facetedPlotData) {

      this.allCharts = [];
      this.chartDrawingStage = ChartDrawingStageEnum.DrawingFirstSetOfCharts;
      let facet_count = this.facetedPlotData.length;
      this.config = this.get_config();

      // Get the layouts, primary layout is used for plots at the start of each row, secondary layout is for subsequent plots
      let [layout_primary, layout_secondary] = this.get_plot_layouts();

      this.facetLayoutStyle = this.set_grid_sizes(facet_count);
      const numberOfColumns = this.get_columns(facet_count);

      const rows = Math.ceil(facet_count / numberOfColumns);
      const total_inter_row_gap = (rows - 1) * 2 * this.FACET_GAP;

      // The greater of the min height, or the canvas height (less margins)
      // divided by the number of rows.
      const height = Math.max(
        MIN_HEIGHT + this.getAnnotationHeightOffset(),
        (this.canvasHeight -
          CHARTVIEW_DISPLAY_PADDING * 2 -
          total_inter_row_gap) /
        rows
      );

      // The greater of the min width, or the canvas width (less margins).
      // This width is only used when there is only one facet, if there are multiple facets the width is determined by the logic in faceted-layout.ts.
      // This is done to fix the shrinking of trend view chart, when view is changed back and forth. (#110418)
      const singleFacetWidth = Math.max(
        MIN_WIDTH,
        (this.canvasWidth - CHARTVIEW_DISPLAY_PADDING) 
      );

      this.facetedPlotData.forEach((current, index) => {
        const layout =
          index % numberOfColumns === 0
            ? Util.deepCopy(layout_primary)
            : Util.deepCopy(layout_secondary);

        // For presummarised indicator, show the layout title even if there is atleast one group not in the compare.
        // And for classic indicator, hide the layout title if there is only one chart
        if ((this.isPresummarised && this.state.Compare.length != this.state.Groups.length) || this.facetedPlotData.length > 1) {
          //layout.title = this.facetedPlotData[index].facet_title;
          layout.title = {
            //text: this.makeTwoLines(this.facetedPlotData[index].facet_title),
            text: !!this.facetedPlotData[index] ? this.facetedPlotData[index].facet_title : '',
            xanchor: "auto",
            yanchor: "bottom",
            x: 0,
            xref: "paper",
            yref: "paper",
            font: {
              size: 15,
            },
          };
        }
        const chart = new Chart(
          layout,
          !!this.facetedPlotData[index] ? this.facetedPlotData[index].traces : null,
          height,
          facet_count > 1 ? null : singleFacetWidth
        );
        chart.Id = `trend-view-chart-${index + 1}`;
        this.allCharts.push(chart);
      });

      // draw
      const tmpCharts: Chart[] = [];
      this.charts = [];
      for (let chart of this.allCharts) {
        tmpCharts.push(chart);
      }
      this.charts = tmpCharts;
      this.cdRef.detectChanges();
    }
  }

  drawLegend() {
    this.buildFacetedPlotData();

    if (!!this.facetedPlotData) {
      this.showLegend =
        Array.isArray(this.facetedPlotData) &&
        this.facetedPlotData.length > 0 &&
        !!this.facetedPlotData[0] &&
        !!this.facetedPlotData[0].traces &&
        // Hide the legend if there is nothing in compare. 
        this.state.Compare.length !== 0;

      if (this.legend && !!this.facetedPlotData[0]) {
        this.legend.onStateUpdated(this.facetedPlotData[0].traces, this.isPresummarised);
      }

      this.chartDrawingStage = ChartDrawingStageEnum.DrawLegend;
    }
  }

  buildFacetedPlotData() {
    let plot_data = [];
    let faceted_plot_data = [];
    // Init the colour bank which provides the grouped colours to apply to traces
    let colour_bank = new IndicatorColourBank();

    // Init variable used to track which colours are assigned to which traces
    let trace_colours = {};

    // Collect all unique periods (including system-generated) for x-axis display
    this.allPeriods = Array.from(new Set(
      this.state.FilteredData.map(record => record.Period)
    )).sort((a, b) => Number(a) - Number(b)) as number[];

    this.state.FilteredData.forEach((record) => {
      // Skip system-generated records from traces
      if (record.IsSystemGenerated === true) {
        return;
      }

      let trace_id = this.get_trace_id(record);
      let facet_id = FacetsLib.get_facet_id(record, this.state);
      let facet_title = FacetsLib.get_facet_title(record, this.state, this.isPresummarised);

      let facet = faceted_plot_data.find(
        (element) => element.facet_id === facet_id
      );

      if (facet === undefined) {
        facet = {
          facet_id,
          facet_title,
          traces: [],
        };
        faceted_plot_data.push(facet);
      }

      // Get any existing trace to
      let trace = plot_data.find((element) => element.id === trace_id);

      // If there was no trace for this data record then create one
      var filteredMeasures = this.measureService.get_filtered_measures(this.state);
      let measure = filteredMeasures.find(
        (x) => x.name == this.state.Measure[0]
      );
      const yValue = record[this.state.Measure[0]];
      let decimalPoints = this.state.IsFertilityRatesIndicator ? 2 : (Util.checkIfInteger(yValue) === true ? '0' : '1');

      let hoverTemplate = this.isPresummarised ? "<b>%{text[0]}</b><br>" + measure.description + ": %{text[3]}<br>" :
        "<b>%{text[0]}</b><br>" + measure.description + ": %{y:." + decimalPoints + "f}<br>";
      const confidenceInterval = this.state.ConfidenceIntervals[measure.name];

      if (trace === undefined) {
        trace = {
          x: [],
          y: [],
          mode: "lines+markers",
          text: [],
        };

        trace.id = trace_id;
        trace.showlegend = false;
        trace.facet_id = facet_id;
        plot_data.push(trace);
        facet.traces.push(trace);
      }

      // ---- Add Legend ----
      let legend_group = this.get_legend_group(record);
      let legend_name = this.get_legend_name(record);
      let legend_id = [legend_group, legend_name].join("--");

      // Add the data point
      trace.x.push(this.getPeriodLabel(record.Period, record.Annotation));
      trace.y.push(record[this.state.Measure[0]]);
      let lowerCiValue, upperCiValue;
      if (!!confidenceInterval) {
        lowerCiValue = record[confidenceInterval.lower.name];
        upperCiValue = record[confidenceInterval.upper.name];

        if (this.state.Confidence) {
          const lowerCiDecimalPoints = this.state.IsFertilityRatesIndicator ? 2 : (Util.checkIfInteger(lowerCiValue) === true ? '0' : '1');
          const upperCiDecimalPoints = this.state.IsFertilityRatesIndicator ? 2 : (Util.checkIfInteger(upperCiValue) === true ? '0' : '1');

          hoverTemplate += this.isPresummarised ? `${confidenceInterval.lower.description}: %{text[1]}` : `${confidenceInterval.lower.description}: %{text[1]:.` + lowerCiDecimalPoints + `f}`;
          hoverTemplate += `<br>`;
          hoverTemplate += this.isPresummarised ? `${confidenceInterval.upper.description}: %{text[2]}` : `${confidenceInterval.upper.description}: %{text[2]:.` + upperCiDecimalPoints + `f}`;
        }
      }
      trace.text.push([legend_name, lowerCiValue, upperCiValue, yValue]);

      trace.hovertemplate = hoverTemplate;

      let colour = trace_colours[legend_id];
      if (colour === undefined) {
        colour = colour_bank.take_colour_data(legend_group);
        trace_colours[legend_id] = colour;
      }

      //trace.name = legend_name;
      trace.name = "";
      trace.legendgroup = legend_group;
      trace.marker = { color: colour_bank.format_as_hsl(colour) };

      // If showing confidence intervals
      if (this.state.Confidence && !!confidenceInterval) {
        let upperLimitTrace = plot_data.find(
          (element) => element.id === trace_id + "-upper-limit-trace"
        );
        if (upperLimitTrace === undefined) {
          upperLimitTrace = {
            x: [],
            y: [],
            fill: "tonexty",
            fillcolor: colour_bank.format_as_hsla(colour, 0.1),
            line: { color: "transparent" },
            mode: "lines",
            //hovertemplate: ''
            hoverinfo: "skip",
          };
          upperLimitTrace.id = trace_id + "-upper-limit-trace";
          upperLimitTrace.showlegend = false;
          upperLimitTrace.facet_id = facet_id;
          upperLimitTrace.name = "Confidence";
          plot_data.push(upperLimitTrace);
          facet.traces.push(upperLimitTrace);
        }
        upperLimitTrace.x.push(this.getPeriodLabel(record.Period, record.Annotation));
        upperLimitTrace.y.push(upperCiValue);

        let lowerLimitTrace = plot_data.find(
          (element) => element.id === trace_id + "-lower-limit-trace"
        );
        if (lowerLimitTrace === undefined) {
          lowerLimitTrace = {
            x: [],
            y: [],
            fill: "tonexty",
            fillcolor: colour_bank.format_as_hsla(colour, 0.3),
            line: { color: "transparent" },
            mode: "lines",
            hoverinfo: "skip",
          };
          lowerLimitTrace.id = trace_id + "-lower-limit-trace";
          lowerLimitTrace.showlegend = false;
          lowerLimitTrace.facet_id = facet_id;
          lowerLimitTrace.name = "Confidence";
          plot_data.push(lowerLimitTrace);
          facet.traces.push(lowerLimitTrace);
        }
        lowerLimitTrace.x.push(this.getPeriodLabel(record.Period, record.Annotation));
        lowerLimitTrace.y.push(lowerCiValue);
      }
    });

    this.facetedPlotData = faceted_plot_data;

    if (!this.shouldAddMissingPeriods) return;

    // Add invisible dummy traces to ensure all periods show on x-axis
    this.addDummyTracesForAllPeriods();

    // Add vertical dotted lines for periods without data points
    // This needs to be done after facetedPlotData is set so max_yaxis_value() works
    this.addVerticalLinesForMissingPeriods();
  }

  private addDummyTracesForAllPeriods() {
    if (!this.facetedPlotData || this.facetedPlotData.length === 0) return;

    this.facetedPlotData.forEach(facet => {
      // Add a dummy trace with all periods but null values
      // This forces Plotly to show all periods on the x-axis without any visible markers or lines
      const dummyTrace = {
        x: this.allPeriods.map(p => this.getPeriodLabel(p, this.annotations[p])),
        y: Array(this.allPeriods.length).fill(null),
        mode: 'markers',
        marker: { opacity: 0 },
        showlegend: false,
        hoverinfo: 'none',
        name: 'dummy-all-periods',
      };

      facet.traces.unshift(dummyTrace); // Add at the beginning
    });
  }

  private addVerticalLinesForMissingPeriods() {
    if (!this.facetedPlotData || this.facetedPlotData.length === 0) return;

    const maxY = this.max_yaxis_value();

    this.facetedPlotData.forEach(facet => {
      // Find the main data trace (lines+markers)
      const dataTraces = facet.traces.filter(trace => trace.mode === "lines+markers");

      if (dataTraces.length === 0) return;

      // Collect all periods that have data points across all data traces in this facet
      const periodsWithData = new Set();
      dataTraces.forEach(trace => {
        if (trace.x && Array.isArray(trace.x)) {
          trace.x.forEach(period => periodsWithData.add(period.toString()));
        }
      });

      // Find periods that are in allPeriods but not in the data
      const missingPeriods = this.allPeriods.filter(
        period => !periodsWithData.has(this.getPeriodLabel(period, this.annotations[period]))
      );

      // Add vertical dotted lines for missing periods
      if (missingPeriods.length > 0) {
        missingPeriods.forEach(period => {
          const verticalLineTrace = {
            x: [this.getPeriodLabel(period, this.annotations[period]), this.getPeriodLabel(period, this.annotations[period])],
            y: [0, maxY],
            mode: 'lines',
            line: {
              color: 'rgba(128, 128, 128, 0.5)',
              dash: 'dot',
              width: 1
            },
            showlegend: false,
            hoverinfo: 'skip',
            name: 'missing-period-line',
          };
          facet.traces.push(verticalLineTrace);
        });
      }
    });
  }

  calculate_column_widths(columns) {
    let avaliable_space = this.get_avaliable_width(columns);
    return [
      Math.round(
        avaliable_space / columns + Y_AXIS_OFFSET - Y_AXIS_OFFSET / columns
      ),
      Math.round(avaliable_space / columns - Y_AXIS_OFFSET / columns),
    ];
  }

  get_config() {
    return {
      responsive: true,
      modeBarButtonsToRemove: [
        "toImage",
        "pan2d",
        "select2d",
        "lasso2d",
        "zoomIn2d",
        "zoomOut2d",
        "autoScale2d",
        "hoverClosestCartesian",
        "hoverCompareCartesian",
      ],
      displaylogo: false,
    };
  }

  get_plot_layouts() {
    const state = this.state;
    const max = this.max_yaxis_value() * YAXIS_HEADROOM;

    const xaxis1: any = {
      type: "category",
      fixedrange: true,
      automargin: true
    };
    if (this.shouldAddMissingPeriods) {
      xaxis1.categoryorder = "array";
      xaxis1.categoryarray = this.allPeriods.map(period => this.getPeriodLabel(period, this.annotations[period]));
    }

    const layout1 = {
      margin: { l: 64, r: 0, t: 64, pad: 4 },
      xaxis: xaxis1,
      yaxis: {
        autorange: false,
        range: [0, max],
        fixedrange: true,
      },
      hovermode: "closest",
    };

    let layout_primary = JSON.parse(JSON.stringify(layout1));

    if (state.Meta.measures !== undefined) {
      var filteredMeasures = this.measureService.get_filtered_measures(this.state);
      layout_primary.yaxis.title = {
        text: filteredMeasures.find(
          (element) => element.name === state.Measure[0]
        ).description,
        standoff: 8,
      };
    }

    const xaxis2: any = {
      type: "category",
      fixedrange: true,
      automargin: true
    };
    if (this.shouldAddMissingPeriods) {
      xaxis2.categoryorder = "array";
      xaxis2.categoryarray = this.allPeriods.map(period => this.getPeriodLabel(period, this.annotations[period]));
    }

    const layout2 = {
      margin: { l: 64, r: 0, t: 64, pad: 4 },
      xaxis: xaxis2,
      yaxis: {
        fixedrange: true,
        autorange: false,
        range: [0, max],
      },
      hovermode: "closest",
    };

    let layout_secondary = JSON.parse(JSON.stringify(layout2));
    layout_secondary.yaxis.showticklabels = false;
    layout_secondary.margin.l = 0;

    return [layout_primary, layout_secondary];
  }

  max_yaxis_value() {
    const faceted_plot_data = this.facetedPlotData;

    return faceted_plot_data.reduce((accumulator, facet) => {
      return Math.max(
        accumulator,
        facet.traces.reduce((accumulator, trace) => {
          return Math.max(accumulator, ...trace.y);
        }, 0)
      );
    }, 0);
  }

  get_trace_id(record) {
    const state = this.state;

    let trace_id = [state.Location, ...state.Groups]
      .reduce(
        (result, group) => {
          result.push(record[group]);
          return result;
        },
        [record[state.Location]]
      )
      .join("--");

    trace_id = Util.slugify(trace_id);

    return trace_id;
  }

  get_legend_name(record) {
    const compare = this.state.Compare;

    let primary_compare = compare.slice(0).shift();

    let legend_name = compare
      .reduce((accumulator, current) => {
        if (compare.length === 1 || current !== primary_compare) {
          accumulator.push(record[current]);
        }
        return accumulator;
      }, [])
      .join(" - ");

    if (legend_name === "") {
      legend_name = "NSW";
    }
    return legend_name;
  }

  get_legend_group(record) {
    const compare = this.state.Compare;

    let primary_compare = compare.slice(0).shift();
    let legend_group = compare.length > 1 ? record[primary_compare] : "-";
    return legend_group;
  }

  // Returns the period label, with annotation if applicable. If the indicator is presummarised and an annotation exists for the period, it returns the period with a superscript annotation. Otherwise, it returns just the period.
  private getPeriodLabel(period: number | string, annotation: string = null) {
    if (this.isPresummarised && annotation) return `${period}<sup>${annotation}</sup>`;

    return period;
  }

  // Returns the height offset needed for annotations, based on the longest annotation present in the data.
  // Each annotation is assumed to take up 10px of height, but the total offset is capped at MAX_ANNOTATION_OFFSET.
  getAnnotationHeightOffset() {
    const maxAnnotationLength = Object.keys(this.annotations).reduce(
      (maxLength, period) => Math.max(maxLength, this.annotations[period]?.length || 0),
      0
    );

    return Math.min(maxAnnotationLength * 10, MAX_ANNOTATION_OFFSET);
  }
}
